import { AlpineData } from "@/js/interfaces/Components/alpinejs/Modal";
import { createAlpineComponent } from "@nolikein/typed-alpinejs";
import { EventManager } from "../../services/EventManager";

/* Generated from the "artisan make:alpine" command */
export default {
    name: 'Modal',
    component: (
        // Component parameters...
        id: string,
    ) => {
        const trimmedId = id.trim();
        if (!trimmedId) {
            throw new Error('Modal: id must not be empty');
        }

        return createAlpineComponent<AlpineData>({
            // Component properties ...
            show: false,
            id: trimmedId,
            events: new EventManager(),

            /**
             * Initialize the component
             */
            init() {
                this.events.on('show-modal', (event: Event) => {
                    if (this.shouldProduceAction(event as CustomEvent)) {
                        this.open();
                    }
                });

                this.events.on('close-modal', (event: Event) => {
                    if (this.shouldProduceAction(event as CustomEvent)) {
                        this.close();
                    }
                });

                this.events.on('keydown', (event: Event) => {
                    const keyboardEvent = event as KeyboardEvent;
                    if ('Escape' === keyboardEvent.key && this.show) {
                        this.close();
                    }
                });

                // Check when click outside of the modal content or on the backdrop to close it
                this.events.on('click', (event: Event) => {
                    const dialog = this.$refs['dialog'] as HTMLDialogElement;
                    const backdrop = this.$refs['backdrop'] as HTMLElement;

                    // Close when clicking directly on the custom backdrop
                    if (event.target === backdrop) {
                        this.close();
                        return;
                    }

                    // Close when clicking inside the dialog element but outside the modal content div
                    const clickedDialog = (event.target as HTMLElement).closest('dialog');
                    if (clickedDialog === dialog) {
                        if (!dialog.children[0]?.contains(event.target as Node)) {
                            this.close();
                        }
                    }
                });
            },

            destroy() {
                this.events.removeEventListeners();
            },
            open(): void {
                this.show = true;
                this.$nextTick(() => {
                    const dialog = this.$refs['dialog'] as HTMLDialogElement;
                    const backdrop = this.$refs['backdrop'] as HTMLElement;

                    // Use show() instead of showModal() so the dialog stays in the normal stacking context,
                    // allowing fixed-position elements (e.g. notifications) with a higher z-index to render above.
                    dialog.show();
                    dialog.classList.add('open-modal');
                    dialog.classList.remove('close-modal');

                    backdrop.classList.remove('hide');
                    backdrop.classList.add('open-modal');
                    backdrop.classList.remove('close-modal');
                });
            },

            close(): void {
                this.show = false;
                this.$nextTick(() => {
                    const dialog = this.$refs['dialog'] as HTMLDialogElement;
                    const backdrop = this.$refs['backdrop'] as HTMLElement;

                    dialog.classList.remove('open-modal');
                    dialog.classList.add('close-modal');
                    backdrop.classList.remove('open-modal');
                    backdrop.classList.add('close-modal');

                    const animationDuration = parseFloat(getComputedStyle(dialog).animationDuration) * 1000 || 0;
                    const timeout = setTimeout(() => {
                        dialog.close();
                        backdrop.classList.add('hide');
                        clearTimeout(timeout);
                    }, animationDuration);
                });
            },

            shouldProduceAction(event: CustomEvent): boolean {
                // Check if the event has an id and if the id is the same as the component id
                return event.detail?.id?.trim() === this.id;
            },
        });
    },
};
